/* eslint-disable prettier/prettier */
import { useState, useEffect, useMemo } from 'react';
import { Paginator, Message, Participant, Conversation } from '@twilio/conversations';

import { useChat } from 'hooks';

export function useChatState(isGroup = false) {
  const { identity, conversation, roomConversation } = useChat();

  const [messages, setMessages] = useState<Message[]>([]);
  const [participants, setParticipants] = useState<Map<string, string>>(new Map());
  const [typingParticipants, setTypingParticipants] = useState<string[]>([]);

  useEffect(() => {
    const currentConversation: Conversation | null = isGroup ? roomConversation : conversation;

    if (currentConversation) {
      currentConversation!
        .getMessages(999)
        .then((paginator: Paginator<Message>) => {
          setMessages(paginator.items);
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.log(`Fetch messages failed with error: ${error}`);
        });

      currentConversation!.getParticipants().then((participants: Participant[]) => {
        const participantsMap: Map<string, string> = new Map();

        participants.forEach((participant: Participant) => {
          const identity = participant.identity ?? '';
          const rawAttrs = participant.attributes;
          let displayName = identity;

          if (typeof rawAttrs === 'string') {
            // ✅ Do NOT parse string — just use it directly
            displayName = rawAttrs;
          } else if (typeof rawAttrs === 'object' && rawAttrs !== null && !Array.isArray(rawAttrs)) {
            const name =
              (rawAttrs as Record<string, unknown>).displayName ?? (rawAttrs as Record<string, unknown>).userName;
            displayName = typeof name === 'string' ? name : identity;
          }

          participantsMap.set(identity, displayName);
        });

        setParticipants(participantsMap);
      });

      currentConversation.on('participantJoined', onParticipantJoined);
      // conversation.conversation.on('participantUpdated', onParticipantUpdated);
      currentConversation.on('messageAdded', onMessageAdded);
      currentConversation.on('typingStarted', onTypingStarted);
      currentConversation.on('typingEnded', onTypingEnded);

      return () => {
        currentConversation.off('participantJoined', onParticipantJoined);
        // conversation.conversation.off('participantUpdated', onParticipantUpdated);
        currentConversation.off('messageAdded', onMessageAdded);
        currentConversation.off('typingStarted', onTypingStarted);
        currentConversation.off('typingEnded', onTypingEnded);
      };
    }
  }, [conversation, roomConversation]);

  const onParticipantJoined = (newParticipant: Participant) => {
    const identity = newParticipant.identity ?? '';
    let displayName = identity;

    try {
      const rawAttrs = newParticipant.attributes;

      if (typeof rawAttrs === 'string') {
        // ✅ fallback if it's just a name like "Banana"
        displayName = rawAttrs;
      } else if (typeof rawAttrs === 'object' && rawAttrs !== null && !Array.isArray(rawAttrs)) {
        const name =
          (rawAttrs as Record<string, unknown>).displayName ?? (rawAttrs as Record<string, unknown>).userName;
        displayName = typeof name === 'string' ? name : identity;
      }
    } catch (e) {
      console.warn(`⚠️ Failed to parse attributes for ${identity}:`, e);
    }

    setParticipants((prev: Map<string, string>) => {
      const newMap = new Map(prev);
      newMap.set(identity, displayName);
      return newMap;
    });

    // 🔁 Schedule async enrichment for late joiners
    setTimeout(async () => {
      try {
        const userRecord = await newParticipant.getUser();
        const attrs = userRecord.attributes;
        let enrichedName = identity;

        if (typeof attrs === 'string') {
          enrichedName = attrs;
        } else if (typeof attrs === 'object' && attrs !== null && !Array.isArray(attrs)) {
          const name = (attrs as Record<string, unknown>).displayName ?? (attrs as Record<string, unknown>).userName;
          enrichedName = typeof name === 'string' ? name : identity;
        }

        setParticipants((prev: Map<string, string>) => {
          const updatedMap = new Map(prev);
          updatedMap.set(identity, enrichedName);
          return updatedMap;
        });
      } catch (e) {
        console.warn(`⏳ Failed to enrich participant ${identity}:`, e);
      }
    }, 0);
  };

  const onMessageAdded = (newMessage: Message) => {
    setMessages((messages: Message[]) => [...messages, newMessage]);
  };

  const onTypingStarted = (newParticipant: Participant) => {
    setTypingParticipants((prevParticipants: string[]) => [...prevParticipants, newParticipant.identity!]);
  };

  const onTypingEnded = (participantToRemove: Participant) => {
    setTypingParticipants((prevParticipants: string[]) => [
      ...prevParticipants.filter((participant: string) => participant !== participantToRemove.identity),
    ]);
  };

  const typingUserName = useMemo((): string => {
    const typingParticipantIdentity: string | undefined = typingParticipants.find(
      (participant: string) => participant !== identity,
    );

    return typingParticipantIdentity ? participants.get(typingParticipantIdentity) ?? typingParticipantIdentity : '';
  }, [typingParticipants, participants]);

  const chatName = useMemo((): string => {
    if (isGroup && participants.size !== 2) {
      return 'Chat';
    }

    const chatName: string | undefined = participants.get(
      [...participants.keys()].find((key: any) => key !== identity) ?? '',
    );
    return chatName ?? 'Dialogue';
  }, [conversation, participants]);

  return { chatName, messages, participants, typingUserName };
}
